// =============================================================================
// ui/tabs/IngestKeys.tsx — My Account → Realtime Sync.
// -----------------------------------------------------------------------------
// ── 🚨 THE KEY IS A BEARER SECRET, AND EVERY DECISION HERE FOLLOWS FROM THAT ──
//
// Anyone holding it can POST trades into this account from anywhere. It is shown
// ONCE and stored only as a hash, so there is no recovery — the only remedy for a
// leak is revoke and regenerate.
//
// Three consequences, each of which would be over-engineering for anything else:
//
//   1. AN ACKNOWLEDGEMENT GATE stands in front of Generate — a tick-box the user
//      must actually tick. Ported from the old client, which added it for the
//      same reason: minting a live secret should not be a single click made
//      while reading something else.
//
//   2. THE PLAINTEXT LIVES IN EXACTLY ONE PIECE OF STATE, inside the modal that
//      shows it, and is dropped when that modal closes. It is never lifted to the
//      tab, never put in a ref "so it can be copied again", never logged.
//
//   3. NOTHING RE-DISPLAYS IT. There is no "show key" control anywhere, because
//      there is nothing to show — the database holds a hash.
//
// ⚠ THE COPY BUTTON IS THE ONLY EGRESS, and it writes to the clipboard, which is
// where the user is about to paste it anyway.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useAccounts } from '../state/accounts';
import { useSupabase } from '../state/supabase';
import { ENV } from '../../env';
import {
  createIngestKey, loadIngestKeys, revokeIngestKey,
} from '../../io/sync/ingestKeys';
import {
  KEY_SOURCES, ingestEndpoint, keyLabelOrPlaceholder, lastUsedLabel, pinLabel,
  type IngestKey,
} from '../../selectors/ingestKeys';
import { orderAccountsForPicker } from '../../selectors/goalForm';
import { reportFailure, reportFailureAs } from '../../io/diagnostics/errorLog';

type Stage = 'list' | 'form' | 'confirm' | 'reveal';

export function RealtimeSyncCard() {
  const { sb, userId } = useSupabase();
  const { accounts } = useAccounts();

  const [keys, setKeys] = useState<readonly IngestKey[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>('list');
  const [draft, setDraft] = useState({ label: '', accountId: '', source: 'atas' });
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [copied, setCopied] = useState<'url' | 'key' | null>(null);

  const endpoint = ingestEndpoint(ENV.config.supabaseUrl);

  const refresh = useCallback(async () => {
    setLoadError(null);
    try {
      setKeys(await loadIngestKeys(sb));
    } catch (e) {
      // ⚠ Keys stay NULL rather than emptying. "No keys yet. Generate one" in
      // front of someone whose key list merely failed to load invites a second
      // live secret they did not need.
      setKeys(null);
      setLoadError(reportFailure('ingest keys', e, 'Couldn’t read your keys.'));
    }
  }, [sb]);

  useEffect(() => { void refresh(); }, [refresh]);

  const nameOf = (id: string): string | null =>
    (accounts ?? []).find((a) => String(a.id) === id)?.name ?? null;

  const copy = async (text: string, what: 'url' | 'key') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
    } catch (e) {
      // ⚠ Says what to do instead rather than failing silently. A clipboard write
      // can be refused by permissions policy, and the user still needs the value.
      //
      // ⚠ `reportFailureAs`, not `reportFailure`: a refused clipboard write
      // rejects with a `DOMException`, which IS an `Error`, so `reportFailure`
      // would return "Write permission denied" and put THAT on screen in place of
      // the instruction. The sentence the user reads must not change.
      setActionError(reportFailureAs(
        'copy to clipboard', e,
        'Couldn’t reach the clipboard — select the text and copy it manually.',
      ));
    }
  };

  const generate = async () => {
    setBusy(true);
    setActionError(null);
    const res = await createIngestKey(sb, userId, {
      label: draft.label,
      accountId: draft.accountId || null,
      source: draft.source,
    });
    setBusy(false);
    if (res.error || !res.plaintext) {
      setActionError(res.error ?? 'Could not create the key.');
      setStage('list');
      return;
    }
    setPlaintext(res.plaintext);
    setStage('reveal');
    void refresh();
  };

  const revoke = async (id: string) => {
    setBusy(true);
    setActionError(null);
    const { error } = await revokeIngestKey(sb, userId, id, new Date().toISOString());
    setBusy(false);
    setConfirmRevoke(null);
    if (error) { setActionError(error); return; }
    void refresh();
  };

  /**
   * ⚠ Drops the plaintext.
   *
   * The guarantee a test can see is that the key leaves the DOM and cannot be
   * shown again — `stage` returns to `list`, and it only goes back to `reveal`
   * immediately after a fresh mint. Clearing the value as well is memory
   * hygiene: it stops a dead secret sitting in a React fiber for the life of the
   * tab, where a heap snapshot or devtools would still find it.
   *
   * ⚠ A sabotage that KEEPS the value leaves every test green, precisely because
   * the stage change already hides it. Keep the clear anyway — it costs a line.
   */
  const closeReveal = () => {
    setPlaintext(null);
    setStage('list');
    setCopied(null);
    setDraft({ label: '', accountId: '', source: 'atas' });
  };

  return (
    <section className="card" data-testid="realtime-sync">
      <h3>Realtime Sync <span className="muted">(ATAS / Sierra Chart)</span></h3>
      <p className="muted">
        Push completed trades straight from your desktop indicator into TRADECOACH
        as they happen. Generate a key, then paste it — with the endpoint below —
        into the indicator’s <b>Cloud Sync</b> settings. Trades appear within
        seconds of going flat. Pin a key to one account, or leave it unpinned to
        route by Broker Account ID. <b>The key is shown once, at creation.</b>
      </p>

      <label className="field">
        <span className="field-label">Ingest endpoint URL</span>
        <span className="row gap-8">
          <input className="ig-url mono" readOnly value={endpoint}
            onFocus={(e) => e.target.select()} />
          <button type="button" className="btn" onClick={() => void copy(endpoint, 'url')}>
            {copied === 'url' ? 'Copied' : 'Copy'}
          </button>
        </span>
      </label>

      {actionError ? <p className="red" role="alert">{actionError}</p> : null}

      <div className="ig-actions">
        <button type="button" className="btn primary" disabled={busy}
          onClick={() => { setActionError(null); setStage('form'); }}>
          + Generate Key
        </button>
      </div>

      <KeyList
        keys={keys}
        loadError={loadError}
        nameOf={nameOf}
        busy={busy}
        confirmRevoke={confirmRevoke}
        onAskRevoke={(id) => { setActionError(null); setConfirmRevoke(id); }}
        onCancelRevoke={() => setConfirmRevoke(null)}
        onRevoke={(id) => void revoke(id)}
        onRetry={() => void refresh()}
      />

      <p className="muted ig-guides">
        Setup guides:{' '}
        <a href="/help/connect-atas.html" target="_blank" rel="noopener">Connect ATAS</a> ·{' '}
        <a href="/help/connect-sierra.html" target="_blank" rel="noopener">Connect Sierra</a> ·{' '}
        <a href="/help/your-ingest-key.html" target="_blank" rel="noopener">Your ingest key</a> ·{' '}
        <a href="/help/one-logger-per-instrument.html" target="_blank" rel="noopener">One logger per instrument</a> ·{' '}
        <a href="/help/my-trades-arent-syncing.html" target="_blank" rel="noopener">Not syncing?</a>
      </p>

      {stage === 'form' ? (
        <NewKeyForm
          draft={draft}
          accounts={accounts}
          onChange={setDraft}
          onCancel={() => setStage('list')}
          onNext={() => setStage('confirm')}
        />
      ) : null}

      {stage === 'confirm' ? (
        <SecurityGate
          busy={busy}
          onCancel={() => setStage('list')}
          onAccept={() => void generate()}
        />
      ) : null}

      {stage === 'reveal' && plaintext ? (
        <RevealOnce
          plaintext={plaintext}
          copied={copied === 'key'}
          onCopy={() => void copy(plaintext, 'key')}
          onDone={closeReveal}
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

function KeyList(
  { keys, loadError, nameOf, busy, confirmRevoke, onAskRevoke, onCancelRevoke, onRevoke, onRetry }: {
    keys: readonly IngestKey[] | null;
    loadError: string | null;
    nameOf: (id: string) => string | null;
    busy: boolean;
    confirmRevoke: string | null;
    onAskRevoke: (id: string) => void;
    onCancelRevoke: () => void;
    onRevoke: (id: string) => void;
    onRetry: () => void;
  },
) {
  if (loadError) {
    return (
      <p className="muted ig-empty" role="alert">
        Couldn’t load your keys. {loadError}{' '}
        <button type="button" className="btn" onClick={onRetry}>Try again</button>
      </p>
    );
  }
  if (!keys) return <p className="muted ig-empty">Loading your keys…</p>;
  if (!keys.length) {
    return <p className="muted ig-empty">No keys yet. Generate one to connect your indicator.</p>;
  }

  return (
    <div className="ig-list">
      {keys.map((k) => (
        <div className="ig-row" key={k.id}>
          <div className="grow">
            <div className="mono">
              {/* ⚠ The ellipsis is part of the story: this is a PREFIX, and the
                  rest is gone. Rendering it bare would read as a short key. */}
              <b>{k.keyPrefix}…</b> <span className="tag">{k.source}</span>
            </div>
            <div className="muted ig-meta">
              {keyLabelOrPlaceholder(k.label)} · {pinLabel(k, nameOf)} · {lastUsedLabel(k.lastUsedAt)}
            </div>
          </div>

          {confirmRevoke === k.id ? (
            <>
              <span className="muted ig-confirm">
                Revoke it? Any indicator still using this key stops syncing until
                you paste in a new one.
              </span>
              <button type="button" className="btn" disabled={busy}
                onClick={onCancelRevoke}>Cancel</button>
              <button type="button" className="btn danger" disabled={busy}
                onClick={() => onRevoke(k.id)}>
                {busy ? 'Revoking…' : 'Revoke'}
              </button>
            </>
          ) : (
            <button type="button" className="btn danger" disabled={busy}
              aria-label={`Revoke key ${k.keyPrefix}`}
              onClick={() => onAskRevoke(k.id)}>Revoke</button>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create — form, gate, reveal
// ---------------------------------------------------------------------------

interface Draft { label: string; accountId: string; source: string }

function NewKeyForm(
  { draft, accounts, onChange, onCancel, onNext }: {
    draft: Draft;
    accounts: readonly { id: string; name: string; archived?: boolean; excludeFromMetrics?: boolean }[] | null;
    onChange: (d: Draft) => void;
    onCancel: () => void;
    onNext: () => void;
  },
) {
  return (
    <Modal title="Generate Ingest Key" onClose={onCancel}
      foot={
        <>
          <span className="grow" />
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn primary" onClick={onNext}>Generate</button>
        </>
      }>
      <label className="field">
        <span className="field-label">Label</span>
        <input type="text" placeholder="e.g. ATAS · main PC" value={draft.label}
          onChange={(e) => onChange({ ...draft, label: e.target.value })} />
        <span className="muted field-hint">
          For you, so a list of keys is readable later. Not sent anywhere.
        </span>
      </label>

      <label className="field">
        <span className="field-label">Pin to account</span>
        <select value={draft.accountId}
          onChange={(e) => onChange({ ...draft, accountId: e.target.value })}>
          <option value="">Unpinned — route by Broker Account ID</option>
          {accounts === null ? null : orderAccountsForPicker(accounts).map((a) => (
            <option key={String(a.id)} value={String(a.id)}>
              {a.name}{a.archived ? ' · archived' : ''}
            </option>
          ))}
        </select>
        <span className="muted field-hint">
          Pinned: every trade from this key files into the chosen account.
          Unpinned: routes by the broker account id, and an unknown one prompts
          you to create it.
        </span>
      </label>

      <label className="field">
        <span className="field-label">Platform</span>
        <select value={draft.source}
          onChange={(e) => onChange({ ...draft, source: e.target.value })}>
          {KEY_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </label>
    </Modal>
  );
}

/**
 * The acknowledgement gate.
 *
 * ⚠ THE BUTTON IS DISABLED UNTIL THE BOX IS TICKED, and that is the entire
 * mechanism — it makes the user's hand stop. A gate that can be dismissed with
 * Enter is a paragraph nobody reads.
 */
function SecurityGate(
  { busy, onCancel, onAccept }: { busy: boolean; onCancel: () => void; onAccept: () => void },
) {
  const [acked, setAcked] = useState(false);

  return (
    <Modal title="Before you generate this key" onClose={onCancel}
      foot={
        <>
          <span className="grow" />
          <button type="button" className="btn" disabled={busy} onClick={onCancel}>Cancel</button>
          <button type="button" className="btn primary" disabled={!acked || busy} onClick={onAccept}>
            {busy ? 'Generating…' : 'Generate key'}
          </button>
        </>
      }>
      <div className="ig-warning">
        <div className="ig-warning-icon" aria-hidden="true">⚠️</div>
        <div>
          <b>This key is a live secret — treat it like a password.</b>
          <p>
            Anyone who has it can push trades into your account. It’s shown{' '}
            <b>once</b> and stored only as a hash, so we can’t show it to you again.
          </p>
          <ul>
            <li>Never paste it into a public channel, a screenshot, or a shared doc.</li>
            <li>
              If it’s ever exposed, <b>revoke it</b> here and generate a new one.
            </li>
          </ul>
        </div>
      </div>

      <label className="ig-ack">
        <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} />
        <span>
          I understand — I’ll keep this key secret and revoke it if it’s ever exposed.
        </span>
      </label>
    </Modal>
  );
}

/**
 * The one and only time the key is on screen.
 *
 * ⚠ NO CLOSE BUTTON IN THE HEADER, and the backdrop does nothing. The only way
 * out is the Done button, so the modal cannot be dismissed by the reflex click
 * that closes every other dialog — which here would lose the key permanently.
 */
function RevealOnce(
  { plaintext, copied, onCopy, onDone }: {
    plaintext: string; copied: boolean; onCopy: () => void; onDone: () => void;
  },
) {
  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Your new ingest key">
      <div className="modal">
        <header className="modal-head"><b>Your new ingest key</b></header>
        <div className="modal-body">
          <p className="muted">
            Copy this now — it <b>won’t be shown again</b>. Paste it into your
            indicator’s <b>Cloud Sync → Ingest API Key</b> field.
          </p>
          <div className="row gap-8">
            <input className="ig-plain mono" readOnly value={plaintext}
              aria-label="Your new ingest key"
              onFocus={(e) => e.target.select()} />
            <button type="button" className="btn primary" onClick={onCopy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="muted ig-secret-note">
            🔒 Treat it like a password — anyone with it can write trades to your
            account. Revoke and regenerate here if it is ever exposed.
          </p>
        </div>
        <footer className="modal-foot">
          <span className="grow" />
          <button type="button" className="btn primary" onClick={onDone}>Done</button>
        </footer>
      </div>
    </div>
  );
}

function Modal(
  { title, children, onClose, foot }: {
    title: string; children: React.ReactNode; onClose: () => void; foot: React.ReactNode;
  },
) {
  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal">
        <header className="modal-head">
          <b>{title}</b>
          <button type="button" className="btn" onClick={onClose}>×</button>
        </header>
        <div className="modal-body">{children}</div>
        <footer className="modal-foot">{foot}</footer>
      </div>
    </div>
  );
}
